import { FormInstance } from "antd";
import { DefaultOptionType } from "antd/es/select";
import to from "await-to-js";
import { useContext, useEffect, useState } from "react";
import { ModalsContext } from "../../context/modals.context";
import { ministryService } from "../../modules/ministry/ministry.service";
import { exciseStampsService } from "../../modules/obligee/exciseStamps/exciseStamps.service";
import {
  AkciznaMarkicaKategorijaProzivoda,
  IKategorijaZaZahtevProps,
} from "../../modules/obligee/exciseStamps/exciseStamps.types";

export const useListaKategorijaZaZahtev = ({
  form,
  zahtevFieldName = "brojZahteva",
  kategorijaFieldName = "vrstaKategorijeId",
  isMfin,
  markicaKategorijeId,
}: {
  form: FormInstance;
  zahtevFieldName?: string | string[];
  kategorijaFieldName?: string | string[];
  isMfin?: boolean;
  markicaKategorijeId: AkciznaMarkicaKategorijaProzivoda | null;
}) => {
  const { modals, isModalVisible } = useContext(ModalsContext);
  const [isLoadingKategorije, setIsLoadingKategorije] = useState(false);
  const [isKategorijeDisabled, setIsKategorijeDisabled] = useState(true);
  const [kategorije, setKategorije] = useState<Array<DefaultOptionType>>([]);

  const [error, setError] = useState<{
    message: string | null;
    brojZahteva: string;
  } | null>(null);

  const onBrojZahtevaSubmitSettled = () => {
    form.validateFields([zahtevFieldName]);
  };

  const handleBrojZahtevaChange = async (
    brojZahteva: string
  ): Promise<void | IKategorijaZaZahtevProps | undefined> => {
    form.setFieldValue(zahtevFieldName, brojZahteva);

    if (!brojZahteva) {
      return;
    }

    setIsLoadingKategorije(true);

    const [err, res] = await to(
      isMfin
        ? ministryService.getMfListaKategorijaZaZahtev(brojZahteva)
        : exciseStampsService.getListaKategorijaZaZahtev(
            brojZahteva,
            markicaKategorijeId
          )
    );

    if (err || !res) {
      setIsLoadingKategorije(false);
      setIsKategorijeDisabled(true);
      setKategorije([]);
      setTimeout(() => {
        onBrojZahtevaSubmitSettled();
      });
      return;
    }

    if (!res.success && res.errorMessage && !res.data) {
      setError({ message: res.errorMessage, brojZahteva });
      setIsLoadingKategorije(false);
      setTimeout(() => {
        onBrojZahtevaSubmitSettled();
      });
      return;
    }

    let selectedMarkicaKategorije: IKategorijaZaZahtevProps | undefined =
      undefined;

    if (res.data) {
      setError(null);

      setKategorije(
        res.data?.map((element) => ({
          label: element.markicaKategorijeVrsteIme,
          value: element.markicaKategorijaVrsteId!,
          children:
            element.tipoviPakovanjaZaVrstuKategorije?.map((tip) => ({
              value: tip.tipPakovanjaId,
              label: tip.tipPakovanjaNaziv,
            })) ?? [],
        }))
      );

      if (res.data?.length === 1) {
        form.setFieldValue(
          kategorijaFieldName,
          res.data?.[0]?.markicaKategorijaVrsteId
        );
        await form.validateFields([kategorijaFieldName]);
        selectedMarkicaKategorije = res.data?.[0];
      }

      setIsLoadingKategorije(false);
      setIsKategorijeDisabled(false);

      setTimeout(() => {
        onBrojZahtevaSubmitSettled();
      });

      return selectedMarkicaKategorije;
    }
  };

  const resetKategorije = () => {
    setKategorije([]);
  };

  useEffect(() => {
    if (isMfin && modals.id && isModalVisible("dodajIzmeniEvidencijuModal")) {
      setKategorije([
        {
          label: modals.record.vrstaKategorije,
          value: modals.record.vrstaKategorijeId,
        },
      ]);
    }

    if (isMfin && !isModalVisible("dodajIzmeniEvidencijuModal")) {
      resetKategorije();
      setError(null);
      setIsKategorijeDisabled(true);
    }
  }, [modals.record, modals.id, isModalVisible, isMfin]);

  return {
    isLoadingKategorije,
    isKategorijeDisabled,
    kategorije,
    brojZahtevaError: error,
    handleBrojZahtevaChange,
    resetKategorije,
  };
};
